import React, { useEffect } from 'react';
import {
  GetNotificationsDocument,
  GetNotificationsQuery,
  GetNotificationsQueryVariables,
  GetUnreadNotificationsDocument,
  GetUnreadNotificationsQuery,
  GetUnreadNotificationsQueryVariables,
  useBulkMarkNotificationsAsReadMutation,
  useGetNotificationsQuery,
  useMarkAllNotificationsAsReadMutation
} from '../../../models/graphql';
import { ApolloCache, gql, useApolloClient } from '@apollo/client';
import { NotificationsController, paginateLimit } from './Notifications.controller';
import { NotificationsViewProps } from './Notifications.view';

gql`
  query GetNotifications($orgId: ID!, $limit: Int, $offset: Int) {
    notifications(organizationId: $orgId) {
      count
      items(limit: $limit, offset: $offset) {
        id
        timestamp
        message
        isRead
        name
      }
    }
  }

  mutation MarkAllNotificationsAsRead($orgId: ID!) {
    markAllNotificationsAsRead(organizationId: $orgId)
  }

  mutation BulkMarkNotificationsAsRead($notificationIds: [ID!]!) {
    bulkMarkNotificationsAsRead(notificationIds: $notificationIds) {
      id
      timestamp
      message
      isRead
      name
    }
  }
`;

export const getunreadNotificationCountFromCache = (orgId: string, cache: ApolloCache<unknown>): number | undefined =>
  cache.readQuery<GetUnreadNotificationsQuery, GetUnreadNotificationsQueryVariables>({
    query: GetUnreadNotificationsDocument,
    variables: {
      orgId
    }
  })?.unreadNotificationCount;

export const setUnreadNotificationsQuery = (type: 'one' | 'all', orgId: string, cache: ApolloCache<unknown>): void => {
  const notificationsCount = getunreadNotificationCountFromCache(orgId, cache);
  if (notificationsCount) {
    let newCount: number;
    switch (type) {
      case 'one':
        newCount = notificationsCount - 1;
        break;
      case 'all':
        newCount = 0;
        break;
    }
    cache.writeQuery<GetUnreadNotificationsQuery, GetUnreadNotificationsQueryVariables>({
      query: GetUnreadNotificationsDocument,
      data: {
        unreadNotificationCount: newCount
      },
      variables: {
        orgId
      }
    });
  }
};

export type NotificationsApolloProps = {
  selectedOrganizationId: NotificationsViewProps['selectedOrganizationId'];
};

export const NotificationsApollo = (props: NotificationsApolloProps): JSX.Element => {
  const { selectedOrganizationId } = props;
  const { cache } = useApolloClient();
  const notificationsCount = getunreadNotificationCountFromCache(props.selectedOrganizationId, cache);
  const notificationsResult = useGetNotificationsQuery({
    variables: { orgId: selectedOrganizationId, offset: 0, limit: paginateLimit }
  });
  const { refetch: refetchNotifications } = notificationsResult;
  const unreadCount = notificationsResult.data?.notifications.items.filter(item => !item.isRead).length;

  // in case we already fetched results, we cached them and in the meanwhile new results appear, we need to refetch.
  // we probably don't need to paginate since the new results will appear as they should
  useEffect(() => {
    if (typeof unreadCount === 'number' && notificationsCount && notificationsCount > unreadCount) {
      refetchNotifications();
    }
  }, [notificationsCount, unreadCount, refetchNotifications]);

  const [markAllAsRead] = useMarkAllNotificationsAsReadMutation({
    variables: {
      orgId: selectedOrganizationId
    },
    onCompleted: () => {
      const notificationData = cache.readQuery<GetNotificationsQuery, GetNotificationsQueryVariables>({
        query: GetNotificationsDocument,
        variables: { orgId: selectedOrganizationId }
      });

      if (!notificationData) {
        return;
      }

      // Make a copy of the data but set all read to true.
      const newData = notificationData.notifications.items.map(notification => {
        return { ...notification, isRead: true };
      });

      // Mark all notifications as read in the cache.
      cache.writeQuery<GetNotificationsQuery, GetNotificationsQueryVariables>({
        query: GetNotificationsDocument,
        variables: { orgId: selectedOrganizationId },
        data: {
          notifications: {
            count: notificationData.notifications.count,
            items: newData,
            __typename: 'PorpaginasResult_Notification'
          }
        }
      });
    }
  });

  const [markAsRead] = useBulkMarkNotificationsAsReadMutation({
    onCompleted: result => {
      const notificationData = cache.readQuery<GetNotificationsQuery, GetNotificationsQueryVariables>({
        query: GetNotificationsDocument,
        variables: { orgId: selectedOrganizationId }
      });

      if (!notificationData) {
        return;
      }

      const updatedIds = result.bulkMarkNotificationsAsRead.map(notification => notification.id);

      // Make a copy of the data but set all read to true.
      const newData = notificationData.notifications.items.map(notification => {
        return { ...notification, ...(updatedIds.includes(notification.id) ? { isRead: true } : {}) };
      });

      // Mark all notifications as read in the cache.
      cache.writeQuery<GetNotificationsQuery, GetNotificationsQueryVariables>({
        query: GetNotificationsDocument,
        variables: { orgId: selectedOrganizationId },
        data: {
          notifications: {
            count: notificationData.notifications.count,
            items: newData,
            __typename: 'PorpaginasResult_Notification'
          }
        }
      });
    }
  });

  return (
    <NotificationsController
      {...{
        markAllAsRead,
        markAsRead,
        notificationsResult,
        selectedOrganizationId
      }}
    />
  );
};
